/**
 * Compute a 24-dimensional behavioral context vector from raw interaction
 * buffers.  The vector layout and normalization exactly match the B-JEPA
 * training format documented in jeppe-docs/behavioral-context.md.
 *
 * Kept in sync with:
 *   external-api/src/events/listeners/inference/behavioralContextBuilder.ts
 *
 * Index  Feature                    Scale Factor    Clamp
 * ─────  ─────────────────────────  ──────────────  ─────
 *   0    click_rate                 raw (per sec)   —
 *   1    rapid_click_rate           raw (per sec)   —
 *   2    scroll_rate                raw (per sec)   —
 *   3    scroll_reversal_rate       raw (per sec)   —
 *   4    scroll_depth               ÷ 5000          [0,1]
 *   5    mouse_velocity             ÷ 1000          —
 *   6    mouse_acceleration         ÷ 1000          —
 *   7    velocity_density_ratio     ÷ 100           [0,1]
 *   8    input_rate                 raw (per sec)   —
 *   9    typing_irregularity        ÷ 500           [0,1]
 *  10    avg_input_length           ÷ 50            [0,1]
 *  11    focus_count                ÷ 10            [0,1]
 *  12    focus_blur_ratio           ÷ 2             [0,1]
 *  13    css_animation_rate         ÷ 10            [0,1]
 *  14    canvas_activity_rate       ÷ 5             [0,1]
 *  15    horizon_density            ÷ 50            [0,1]
 *  16    refractory_period          ÷ 2000          [0,1]
 *  17    backtrack_ratio            raw              [0,1]
 *  18    path_straightness          ÷ 5             [0,1]
 *  19    micro_hesitation_count     ÷ 10            [0,1]
 *  20    touch_rate                 ÷ 5             [0,1]
 *  21    selection_count            ÷ 5             [0,1]
 *  22    drag_count                 ÷ 5             [0,1]
 *  23    window_duration            ÷ 10            [0,1]
 */

import type { InteractionBuffers, ElementData } from "./types";

const clamp01 = (v: number): number => Math.min(Math.max(v, 0), 1);

function inTimeWindow<T extends { timestamp: number }>(
  items: T[],
  windowStart: number,
  windowEnd: number,
): T[] {
  return items.filter(
    (item) => item.timestamp >= windowStart && item.timestamp <= windowEnd,
  );
}

export function buildBehavioralVector(
  buffers: InteractionBuffers,
  windowStart: number,
  windowEnd: number,
  elements: ElementData[],
): number[] {
  const duration = (windowEnd - windowStart) / 1000;
  if (duration <= 0) return new Array(24).fill(0);

  const clicks = inTimeWindow(buffers.clicks, windowStart, windowEnd);
  const mousePos = inTimeWindow(buffers.mousePositions, windowStart, windowEnd);
  const scrolls = inTimeWindow(buffers.scrolls, windowStart, windowEnd);
  const inputs = inTimeWindow(buffers.inputs, windowStart, windowEnd);
  const focusBlurs = inTimeWindow(buffers.focusBlurs, windowStart, windowEnd);
  const mutations = inTimeWindow(buffers.mutations, windowStart, windowEnd);
  const touches = inTimeWindow(buffers.touches, windowStart, windowEnd);
  const selections = inTimeWindow(buffers.selections, windowStart, windowEnd);
  const drags = inTimeWindow(buffers.drags, windowStart, windowEnd);
  const styleSheets = inTimeWindow(buffers.styleSheets, windowStart, windowEnd);
  const canvasEvts = inTimeWindow(buffers.canvasEvents, windowStart, windowEnd);

  const clickRate = clicks.length / duration;
  const rapidClickRate = computeRapidClickRate(clicks, duration);
  const scrollRate = scrolls.length / duration;
  const scrollReversalRate = computeScrollReversalRate(scrolls, duration);

  const maxScrollY = scrolls.reduce((max, s) => Math.max(max, s.y), 0);
  const scrollDepth = clamp01(maxScrollY / 5000);

  const { avgVelocity, avgAcceleration } = computeMouseDynamics(mousePos);
  const mouseVelocity = avgVelocity / 1000;
  const mouseAcceleration = avgAcceleration / 1000;

  const interactiveCount = elements.filter((e) => e.isInteractive).length;
  const horizonDensityRaw = Math.max(interactiveCount, 1);
  const velDensityRatio = clamp01(avgVelocity / horizonDensityRaw / 100);

  const inputRate = inputs.length / duration;
  const typingIrregularity = computeTypingIrregularity(inputs);

  const avgInputLen =
    inputs.length > 0
      ? inputs.reduce((sum, i) => sum + i.text.length, 0) / inputs.length
      : 0;
  const avgInputLengthNorm = clamp01(avgInputLen / 50);

  const focusCount = focusBlurs.filter((f) => f.type === "focus").length;
  const focusCountNorm = clamp01(focusCount / 10);

  const blurCount = focusBlurs.filter((f) => f.type === "blur").length;
  const focusBlurRatio =
    blurCount > 0 ? clamp01(Math.min(focusCount / blurCount, 2) / 2) : 0;

  const cssAnimRate = clamp01(styleSheets.length / duration / 10);
  const canvasRate = clamp01(canvasEvts.length / duration / 5);
  const horizonDensity = clamp01(elements.length / 50);
  const refractoryPeriod = computeRefractoryPeriod(mutations, mousePos);
  const backtrackRatio = computeBacktrackRatio(mousePos, clicks);
  const pathStraightness = computePathStraightness(mousePos);
  const microHesitations = computeMicroHesitations(mousePos);

  const touchRate = clamp01(touches.length / duration / 5);
  const selectionCount = clamp01(selections.length / 5);
  const dragCount = clamp01(drags.length / 5);
  const windowDuration = clamp01(duration / 10);

  return [
    clickRate,
    rapidClickRate,
    scrollRate,
    scrollReversalRate,
    scrollDepth,
    mouseVelocity,
    mouseAcceleration,
    velDensityRatio,
    inputRate,
    typingIrregularity,
    avgInputLengthNorm,
    focusCountNorm,
    focusBlurRatio,
    cssAnimRate,
    canvasRate,
    horizonDensity,
    refractoryPeriod,
    backtrackRatio,
    pathStraightness,
    microHesitations,
    touchRate,
    selectionCount,
    dragCount,
    windowDuration,
  ];
}

function computeRapidClickRate(
  clicks: { timestamp: number; x: number; y: number }[],
  duration: number,
): number {
  if (clicks.length < 2 || duration <= 0) return 0;

  let rapidCount = 0;
  for (let i = 1; i < clicks.length; i++) {
    const dt = clicks[i].timestamp - clicks[i - 1].timestamp;
    const dx = clicks[i].x - clicks[i - 1].x;
    const dy = clicks[i].y - clicks[i - 1].y;
    const dist = Math.sqrt(dx * dx + dy * dy);

    if (dt < 500 && dist < 50) {
      rapidCount++;
    }
  }

  return rapidCount / duration;
}

function computeScrollReversalRate(
  scrolls: { timestamp: number; y: number }[],
  duration: number,
): number {
  if (scrolls.length < 3 || duration <= 0) return 0;

  let reversals = 0;
  let prevDir = 0;

  for (let i = 1; i < scrolls.length; i++) {
    const diff = scrolls[i].y - scrolls[i - 1].y;
    if (diff === 0) continue;

    const dir = diff > 0 ? 1 : -1;
    if (prevDir !== 0 && dir !== prevDir) {
      reversals++;
    }
    prevDir = dir;
  }

  return reversals / duration;
}

function computeMouseDynamics(
  positions: { timestamp: number; x: number; y: number }[],
): {
  avgVelocity: number;
  avgAcceleration: number;
} {
  if (positions.length < 2) return { avgVelocity: 0, avgAcceleration: 0 };

  const velocities: number[] = [];

  for (let i = 1; i < positions.length; i++) {
    const dt = (positions[i].timestamp - positions[i - 1].timestamp) / 1000;
    if (dt < 0.001) continue;

    const dx = positions[i].x - positions[i - 1].x;
    const dy = positions[i].y - positions[i - 1].y;
    const dist = Math.sqrt(dx * dx + dy * dy);

    velocities.push(dist / dt);
  }

  if (velocities.length === 0) return { avgVelocity: 0, avgAcceleration: 0 };

  const avgVelocity = velocities.reduce((s, v) => s + v, 0) / velocities.length;

  let accSum = 0;
  for (let i = 1; i < velocities.length; i++) {
    accSum += Math.abs(velocities[i] - velocities[i - 1]);
  }
  const avgAcceleration =
    velocities.length > 1 ? accSum / (velocities.length - 1) : 0;

  return { avgVelocity, avgAcceleration };
}

function computeTypingIrregularity(
  inputs: { timestamp: number; text: string }[],
): number {
  if (inputs.length < 3) return 0;

  const intervals: number[] = [];
  for (let i = 1; i < inputs.length; i++) {
    intervals.push(inputs[i].timestamp - inputs[i - 1].timestamp);
  }

  const mean = intervals.reduce((s, v) => s + v, 0) / intervals.length;
  const variance =
    intervals.reduce((s, v) => s + (v - mean) ** 2, 0) / intervals.length;
  const stdDev = Math.sqrt(variance);

  return clamp01(stdDev / 500);
}

function computeRefractoryPeriod(
  mutations: { timestamp: number }[],
  mousePos: { timestamp: number }[],
): number {
  if (mutations.length === 0 || mousePos.length === 0) return 0;

  let totalDelay = 0;
  let count = 0;

  for (const mut of mutations) {
    const next = mousePos.find((m) => m.timestamp > mut.timestamp);
    if (next) {
      totalDelay += next.timestamp - mut.timestamp;
      count++;
    }
  }

  if (count === 0) return 0;
  const avgMs = totalDelay / count;
  return clamp01(avgMs / 2000);
}

function computeBacktrackRatio(
  mousePos: { timestamp: number; x: number; y: number }[],
  clicks: { x: number; y: number }[],
): number {
  if (mousePos.length === 0 || clicks.length === 0) return 0;

  let backtracks = 0;

  for (const pos of mousePos) {
    for (const click of clicks) {
      const dx = pos.x - click.x;
      const dy = pos.y - click.y;
      if (Math.sqrt(dx * dx + dy * dy) < 50) {
        backtracks++;
        break;
      }
    }
  }

  return clamp01(backtracks / mousePos.length);
}

function computePathStraightness(
  positions: { x: number; y: number }[],
): number {
  if (positions.length < 2) return 0;

  let totalDist = 0;
  for (let i = 1; i < positions.length; i++) {
    const dx = positions[i].x - positions[i - 1].x;
    const dy = positions[i].y - positions[i - 1].y;
    totalDist += Math.sqrt(dx * dx + dy * dy);
  }

  const first = positions[0];
  const last = positions[positions.length - 1];
  const dx = last.x - first.x;
  const dy = last.y - first.y;
  const straightDist = Math.sqrt(dx * dx + dy * dy);

  if (straightDist < 1) return 0;
  const ratio = totalDist / straightDist;
  return clamp01(ratio / 5);
}

function computeMicroHesitations(positions: { timestamp: number }[]): number {
  if (positions.length < 2) return 0;

  let hesitations = 0;

  for (let i = 1; i < positions.length; i++) {
    const gap = positions[i].timestamp - positions[i - 1].timestamp;
    if (gap >= 200 && gap <= 400) {
      hesitations++;
    }
  }

  return clamp01(hesitations / 10);
}
